import { Form } from "antd"
import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import type { SetupWorkplace } from "../setup/components/SetupWorkplaceList"
import {
  SETUP_WORKPLACE_QUERY_KEY,
  type CreateWorkplaceBody,
  type UpdateWorkplaceBody,
  type WorkplaceFormValues,
} from "../typings"

// Form values expected by the create/configure workplace drawer

type Params = {
  setCurrentStep: (step: number) => void
  workplacesQueryParams: string
}

export function useWorkplaceDrawer({
  setCurrentStep,
  workplacesQueryParams,
}: Params) {
  const [createWorkplaceDrawerOpen, setCreateWorkplaceDrawerOpen] =
    useState(false)
  const [configureAddressWorkplace, setConfigureAddressWorkplace] =
    useState<SetupWorkplace | null>(null)
  const [createWorkplaceForm] = Form.useForm<WorkplaceFormValues>()
  const [editWorkplace, setEditWorkplace] = useState<SetupWorkplace | null>(
    null
  )

  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()

  const closeCreateWorkplaceDrawer = () => {
    setCreateWorkplaceDrawerOpen(false)
    setConfigureAddressWorkplace(null)
    createWorkplaceForm.resetFields()
  }

  const { mutate: createWorkplace, isLoading: isCreatingWorkplace } =
    useMutation({
      mutationFn: async (body: CreateWorkplaceBody) => {
        const res = await newArchAxiosInstance.post(
          "/admin/company/add_officeaddress",
          body
        )
        return res.data
      },
      onSuccess: () => {
        toast.success("Workplace created successfully")
        queryClient.invalidateQueries({
          queryKey: ["admin/company/view/locations"],
        })
        queryClient.invalidateQueries({
          queryKey: [`/v1/time-attendance/workplaces?${workplacesQueryParams}`],
        })
        closeCreateWorkplaceDrawer()
      },
      onError: onQueryError,
    })

  const { mutate: updateWorkplace, isLoading: isUpdatingWorkplace } =
    useMutation({
      mutationFn: async (body: UpdateWorkplaceBody) => {
        const res = await newArchAxiosInstance.post(
          `/admin/company/update_officeaddress`,
          body
        )
        return res.data
      },
      onSuccess: () => {
        toast.success("Workplace updated successfully")
        queryClient.invalidateQueries({
          queryKey: ["admin/company/view/locations"],
        })
        queryClient.invalidateQueries({
          queryKey: [`/v1/time-attendance/workplaces?${workplacesQueryParams}`],
        })
        closeCreateWorkplaceDrawer()
        queryClient.invalidateQueries({
          queryKey: [SETUP_WORKPLACE_QUERY_KEY],
        })
      },
      onError: onQueryError,
    })

  const openConfigureAddressDrawer = (workplace: SetupWorkplace) => {
    setConfigureAddressWorkplace(workplace)
    setCreateWorkplaceDrawerOpen(true)
    createWorkplaceForm.setFieldsValue({
      workplaceName: workplace.name,
      primaryAddress: workplace.address ?? "",
      city: "",
      country: "",
      state: "",
      taxOffice: "",
      taxOfficeCode: "",
      nhfOffice: "",
      nhfOfficeCode: "",
    })
  }

  const openCreateWorkplaceDrawer = () => {
    setConfigureAddressWorkplace(null)
    setCreateWorkplaceDrawerOpen(true)
    createWorkplaceForm.resetFields()
  }

  const handleConfigureWorkplace = (formValues: WorkplaceFormValues) => {
    if (!configureAddressWorkplace) return

    updateWorkplace({
      office_address_id: configureAddressWorkplace.id,
      edit_office_address_line_1:
        formValues.primaryAddress ?? configureAddressWorkplace.address ?? "",
      edit_office_address_line_2: "",
      edit_office_address_city: formValues.city ?? "",
      edit_office_address_state_code: formValues.state ?? "",
      edit_office_address_zip_code: "",
      edit_office_address_country_code: formValues.country ?? "",
      edit_office_address_phone: "",
      edit_display_name:
        formValues.workplaceName ?? configureAddressWorkplace.name ?? "",
      edit_tax_office_name: formValues.taxOffice ?? "",
      edit_tax_office_code: formValues.taxOfficeCode ?? "",
      edit_housing_location_name: formValues.nhfOffice ?? "",
      edit_housing_location_code: formValues.nhfOfficeCode ?? "",
      edit_latitude:
        formValues.latitude !== undefined ? String(formValues.latitude) : "",
      edit_longitude:
        formValues.longitude !== undefined ? String(formValues.longitude) : "",
      au_comment: "",
    })
  }

  const handleCreateWorkplaceFinish = (values: WorkplaceFormValues) => {
    createWorkplace({
      office_address_line_1: values.primaryAddress || "",
      office_address_state_code: values.state || "",
      display_name: values.workplaceName || "",
      tax_office_name: values.taxOffice || "",
      tax_office_code: values.taxOfficeCode || "",
      housing_location_name: values.nhfOffice || "",
      housing_location_code: values.nhfOfficeCode || "",
      latitude: values.latitude !== undefined ? String(values.latitude) : "",
      longitude: values.longitude !== undefined ? String(values.longitude) : "",
    })
  }

  const handleDrawerFormFinish = (values: WorkplaceFormValues) => {
    if (configureAddressWorkplace) {
      handleConfigureWorkplace(values)
    } else {
      handleCreateWorkplaceFinish(values)
    }
  }
  const openEditWorkplaceDrawer = (workplace: SetupWorkplace) => {
    setEditWorkplace(workplace)
    setConfigureAddressWorkplace(workplace)
    setCreateWorkplaceDrawerOpen(true)
    createWorkplaceForm.setFieldsValue({
      workplaceName: workplace.name ?? "",
      primaryAddress: workplace.address ?? "",
      latitude: workplace.latitude ?? "",
      longitude: workplace.longitude ?? "",
      city: workplace.city ?? "",
      country: workplace.country ?? "",
      state: workplace.state ?? "",
      taxOffice: workplace.taxOfficeName ?? "",
      taxOfficeCode: workplace.taxOfficeCode ?? "",
      nhfOffice: workplace.nhfOfficeName ?? "",
      nhfOfficeCode: workplace.nhfOfficeCode ?? "",
    })
  }

  return {
    createWorkplaceDrawerOpen,
    configureAddressWorkplace,
    createWorkplaceForm,
    openConfigureAddressDrawer,
    openCreateWorkplaceDrawer,
    openEditWorkplaceDrawer,
    closeCreateWorkplaceDrawer,
    handleDrawerFormFinish,
    isCreatingWorkplace,
    isUpdatingWorkplace,
  }
}
